import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_RGB = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌈 CSS RGB Colors</h1>

      <p className="tutorial-intro">
        CSS allows you to define colors using the <code>rgb()</code> function.
        RGB stands for <strong>Red</strong>, <strong>Green</strong>, and{" "}
        <strong>Blue</strong>. Each value ranges from <code>0</code> (none) to{" "}
        <code>255</code> (full intensity).
      </p>

      {/* Basic RGB */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic rgb()</h2>
        <p>
          Syntax: <code>rgb(red, green, blue)</code>
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: rgb(255, 0, 0);   /* Red */
}
p {
  color: rgb(0, 255, 0);   /* Green */
}
div {
  color: rgb(0, 0, 255);   /* Blue */
}`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              backgroundColor: "rgb(255,0,0)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(255,0,0) → Red
          </div>
          <div
            style={{
              backgroundColor: "rgb(0,255,0)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(0,255,0) → Green
          </div>
          <div
            style={{
              backgroundColor: "rgb(0,0,255)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(0,0,255) → Blue
          </div>
        </div>
      </div>

      {/* Shades */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Creating Shades</h2>
        <p>
          You can mix red, green, and blue values to create millions of colors.
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: rgb(255, 165, 0);  /* Orange */
}
div {
  background-color: rgb(128, 0, 128);  /* Purple */
}
div {
  background-color: rgb(0, 128, 128);  /* Teal */
}`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              backgroundColor: "rgb(255,165,0)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(255,165,0) → Orange
          </div>
          <div
            style={{
              backgroundColor: "rgb(128,0,128)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(128,0,128) → Purple
          </div>
          <div
            style={{
              backgroundColor: "rgb(0,128,128)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: "1",
              textAlign: "center",
            }}
          >
            rgb(0,128,128) → Teal
          </div>
        </div>
      </div>

      {/* RGBA */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ RGBA (with Transparency)</h2>
        <p>
          The <code>rgba()</code> function adds a 4th value:{" "}
          <strong>alpha</strong> (opacity). It ranges from <code>0.0</code>{" "}
          (fully transparent) to <code>1.0</code> (fully opaque).
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: rgba(255, 0, 0, 0.3); /* Semi-transparent red */
}`}
        />

        <div
          style={{
            position: "relative",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
            background: "#eee",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(255,0,0,0.3)",
              padding: "1rem",
              borderRadius: "6px",
            }}
          >
            rgba(255,0,0,0.3) → Transparent Red Box
          </div>
          <p style={{ marginTop: "1rem", color: "#444" }}>
            Notice how you can see the gray background through the red box.
          </p>
        </div>
      </div>

      {/* Percentage Values */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Using Percentage Values</h2>
        <p>
          Instead of <code>0–255</code>, you can also use <code>0% – 100%</code>{" "}
          for rgb values.
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: rgb(100%, 0%, 0%);   /* Red */
  background-color: rgb(50%, 50%, 0%);   /* Olive */
}`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              backgroundColor: "rgb(100%,0%,0%)",
              color: "white",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            rgb(100%,0%,0%) → Red
          </div>
          <div
            style={{
              backgroundColor: "rgb(50%,50%,0%)",
              color: "black",
              padding: "1rem",
              borderRadius: "8px",
              flex: 1,
              textAlign: "center",
            }}
          >
            rgb(50%,50%,0%) → Olive
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you understand <code>rgb()</code> and <code>rgba()</code>,
          you can mix colors to create any shade you want. Next, explore{" "}
          <a href="/css/hex" className="learn-more">
            CSS HEX Colors
          </a>{" "}
          to learn another common way of representing colors.
        </p>
      </div>
    </div>
  );
};

export default CSS_RGB;
